import { useEffect, useRef } from "react";
import type {
  MyCandidateFromAPI,
  CandidateStatusEnum,
} from "../../types/candidate";
import { CandidateDetailContent } from "./CandidateDetailContent";
import { useToast } from "../../hooks/useToast";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CandidateDetailModalProps {
  candidate: MyCandidateFromAPI;
  jdPath?: string;
  onClose: () => void;
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  isStatusUpdating?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CandidateDetailModal({
  candidate,
  jdPath,
  onClose,
  onStatusChange,
  isStatusUpdating,
}: CandidateDetailModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Ref to keep the latest onClose without re-running the effect.
  // This avoids the useEffect re-running on every parent re-render
  // (onClose is an inline arrow function with a new identity each render).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // See CandidateListModal for detailed explanation of why we do NOT
  // call dialog.close() in cleanup.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      dialog.showModal();
    }

    const handleClose = () => onCloseRef.current();
    dialog.addEventListener("close", handleClose);
    return () => {
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  const toast = useToast();

  return (
    <dialog
      ref={dialogRef}
      aria-label={`${candidate.full_name} details`}
      className="m-auto flex h-[95dvh] w-full flex-col overflow-hidden rounded-[20px] bg-white lg:h-auto lg:max-h-[85vh] lg:max-w-[1248px] touch-manipulation motion-safe:animate-modal-slide-up backdrop:bg-[rgba(38,38,38,0.4)] backdrop:motion-safe:animate-modal-backdrop-in"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-grey-50 px-3 py-3 lg:px-5 lg:py-4">
        <div className="flex items-center gap-2">
          {/* Back button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Go back"
            className="flex size-10 items-center justify-center rounded-full border border-grey-100 cursor-pointer hover:bg-grey-25 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                d="M12 5L7 10L12 15"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2 className="truncate text-display-md font-semibold tracking-tight text-black-500">
            Candidate Profile
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex size-10 items-center justify-center rounded-full bg-grey-50 border border-black-50 cursor-pointer hover:bg-black-50 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Body — delegates to the reusable CandidateDetailContent */}
      <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-3 lg:p-5">
        <CandidateDetailContent
          candidate={candidate}
          jdPath={jdPath}
          onContactAction={toast.info}
          onStatusChange={onStatusChange}
          isStatusUpdating={isStatusUpdating}
        />
      </div>
    </dialog>
  );
}
